"use client"

import { useEffect } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"

import {
  hasMobileEquivalent,
  isForceDesktop,
  isMobileViewport,
  rememberOutOfScope,
  toMobileRoute,
} from "@/components/mobile/route-map"
import { ROLES, hasRole } from "@/lib/roles"
import { useAuthStore } from "@/stores/auth-store"

/**
 * Null-rendering viewport switch, mounted in app/(dashboard)/layout.tsx — the
 * ONLY change this feature makes to the desktop tree.
 *
 * On mount (and on any pathname change) it checks whether the viewport is
 * phone-sized and, unless the user has chosen "Use desktop site", replaces the
 * current desktop route with its `/m` equivalent.
 *
 * Design notes:
 * - `router.replace`, not `push`: the desktop URL must not sit in history, or
 *   Android back lands on it and immediately re-redirects.
 * - The check runs in an effect, never during render — SSR has no `window`, and
 *   redirecting during render would tear the desktop tree mid-hydration.
 * - Deliberately does NOT subscribe to matchMedia changes. Rotating a tablet
 *   below 768px mid-session should not yank the user out of the page they are
 *   working in; the switch is an entry-point decision. Wave 3's pre-hydration
 *   inline script handles the first-paint flash.
 * - Affiliators are exempt. The role is desktop-only by design and holds none
 *   of the permissions the `/m` tab bar filters on, so bouncing a partner who
 *   opens the app on a phone would strand them on an empty shell with no way
 *   back. They get the responsive desktop tree instead.
 */
export function MobileRedirect() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const roles = useAuthStore((state) => state.user?.roles)
  const workflowEnabled = useAuthStore((state) => state.user?.feature_flags?.tasks_enabled && state.user?.feature_flags?.lead_follow_through_enabled)

  useEffect(() => {
    if (!isMobileViewport()) return
    if (isForceDesktop()) return
    if (hasRole(roles, ROLES.AFFILIATOR)) return

    const current = pathname ?? "/"
    // Cash & banks uses the responsive dashboard shell on phones. There is no
    // separate /m screen; keep all account, register and action detail URLs.
    if (current === "/money" || current.startsWith("/money/")) return
    if (current === "/settings" && searchParams.get("section") === "cash-banks") return
    // Explicit progress links need the full responsive lead workspace.
    if (workflowEnabled && /^\/leads\/[^/]+\/?$/.test(current) && searchParams.get("progress") === "1") return
    // Desktop-only section (hr, reports, settings…): leave a breadcrumb so the
    // mobile tree can explain the bounce instead of silently showing Today.
    if (!hasMobileEquivalent(current)) rememberOutOfScope(current)
    // The query string carries record ids (?conversation=, ?appointment=), so a
    // link to one record opens that record on the phone, not its list.
    router.replace(toMobileRoute(current, searchParams.toString()))
  }, [pathname, roles, router, searchParams, workflowEnabled])

  return null
}
